import { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Chip from "@mui/material/Chip";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import api from "api/axios";
import { libelleVilleDate } from "utils/oumrahVilleDepart";

const defaultForm = {
  libelle: "",
  date_depart: "",
  date_retour: "",
  places_total: "",
  seuil_alerte: 5,
  supplement_carte_sejour: 150,
  description: "",
  tarifs: [
    { libelle: "Chambre double (2 pers)", montant: "", type_chambre: "double" },
    { libelle: "Chambre triple (3 pers)", montant: "", type_chambre: "triple" },
    { libelle: "Chambre quadruple (4 pers)", montant: "", type_chambre: "quadruple" },
  ],
};

const statutColor = {
  ouverte: "#43a047",
  complete: "#fb8c00",
  cloturee: "#9e9e9e",
  archivee: "#757575",
};

function OumrahSessions() {
  const [sessions, setSessions] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [montrerArchivees, setMontrerArchivees] = useState(false);
  const [tarifsOrigine, setTarifsOrigine] = useState([]);
  const [exportSession, setExportSession] = useState(null);
  const [exportFormat, setExportFormat] = useState("excel");
  const [exportStatut, setExportStatut] = useState("");
  const [exportLoading, setExportLoading] = useState(false);
  const [messageSession, setMessageSession] = useState(null);
  const [messageTexte, setMessageTexte] = useState("");
  const [messageLoading, setMessageLoading] = useState(false);
  const [copieOk, setCopieOk] = useState("");
  const [messageType, setMessageType] = useState("publicite");
  const [villesDepart, setVillesDepart] = useState([]);
  const [nouvelleVille, setNouvelleVille] = useState("");
  const [nouvellesPlaces, setNouvellesPlaces] = useState("");
  const [nouvelleDate, setNouvelleDate] = useState("");

  const fmt = (n) => parseFloat(n || 0).toLocaleString("fr-FR");

  const fetchSessions = async () => {
    try {
      const url = "/oumrah/sessions" + (montrerArchivees ? "?archivees=true" : "");
      const res = await api.get(url);
      setSessions(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchSessions();
  }, [montrerArchivees]);

  const handleOpen = (session = null) => {
    if (session) {
      setForm({
        libelle: session.libelle,
        date_depart: session.date_depart?.slice(0, 10) || "",
        date_retour: session.date_retour?.slice(0, 10) || "",
        places_total: session.places_total || "",
        seuil_alerte: session.seuil_alerte ?? 5,
        supplement_carte_sejour: session.supplement_carte_sejour ?? 150,
        description: session.description || "",
        tarifs: session.tarifs?.length ? session.tarifs.map((t) => ({ ...t })) : [{ libelle: "", montant: "", type_chambre: "" }],
      });
      setTarifsOrigine(session.tarifs || []);
      setEditId(session.id);
      chargerVillesDepart(session.id);
    } else {
      setForm(defaultForm);
      setEditId(null);
    }
    setError("");
    setOpen(true);
  };

  const chargerVillesDepart = async (sessionId) => {
    try {
      const res = await api.get("/oumrah/sessions/" + sessionId + "/villes-depart");
      // places_saisie / date_saisie = valeurs affichées dans les champs quota et date (contrôlés).
      // On conserve les saisies non encore enregistrées lors d'un rechargement
      // (ajout/suppression d'une autre ville).
      setVillesDepart((prev) =>
        res.data.map((v) => {
          const ancien = prev.find((p) => p.id === v.id);
          const saisieEnCours = ancien && ancien.places_saisie !== String(ancien.places_total || 0);
          const dateEnCours = ancien && ancien.date_saisie !== (ancien.date_depart || "");
          return {
            ...v,
            places_saisie: saisieEnCours ? ancien.places_saisie : String(v.places_total || 0),
            date_saisie: dateEnCours ? ancien.date_saisie : v.date_depart || "",
          };
        })
      );
    } catch (err) {
      console.error("Chargement villes depart:", err);
    }
  };

  const ajouterVilleDepart = async () => {
    if (!nouvelleVille.trim() || !editId) return;
    try {
      await api.post("/oumrah/sessions/" + editId + "/villes-depart", {
        ville: nouvelleVille.trim(),
        ordre: villesDepart.length,
        places_total: nouvellesPlaces ? parseInt(nouvellesPlaces) : 0,
        date_depart: nouvelleDate || null,
      });
      setNouvelleVille("");
      setNouvellesPlaces("");
      setNouvelleDate("");
      chargerVillesDepart(editId);
    } catch (err) {
      alert(err.response?.data?.message || "Erreur lors de l'ajout de la ville");
    }
  };
  const placesModifiees = (v) => parseInt(v.places_saisie || 0) !== (v.places_total || 0);
  const dateModifiee = (v) => (v.date_saisie || "") !== (v.date_depart || "");
  const villeModifiee = (v) => placesModifiees(v) || dateModifiee(v);
  // N'envoie que ce qui a changé (quota et/ou date ; date vide = effacée)
  const modifierVille = async (v) => {
    const champs = {};
    if (placesModifiees(v)) champs.places_total = v.places_saisie === "" ? 0 : parseInt(v.places_saisie);
    if (dateModifiee(v)) champs.date_depart = v.date_saisie || null;
    await api.put("/oumrah/sessions/" + editId + "/villes-depart/" + v.id, champs);
  };
  const changerSaisieVille = (villeId, champ, valeur) => {
    setVillesDepart((prev) => prev.map((v) => (v.id === villeId ? { ...v, [champ]: valeur } : v)));
  };
  const enregistrerSaisieVille = async (v) => {
    if (!villeModifiee(v)) return;
    try {
      await modifierVille(v);
      chargerVillesDepart(editId);
    } catch (err) {
      alert(err.response?.data?.message || "Erreur lors de la mise à jour de la ville de départ");
    }
  };

  const supprimerVilleDepart = async (villeId) => {
    if (!window.confirm("Supprimer cette ville de départ ? Les tarifs qui y sont liés perdront cette association.")) return;
    try {
      await api.delete("/oumrah/sessions/" + editId + "/villes-depart/" + villeId);
      chargerVillesDepart(editId);
    } catch (err) {
      alert(err.response?.data?.message || "Erreur lors de la suppression");
    }
  };

  const handleClose = () => {
    setOpen(false);
    setForm(defaultForm);
    setEditId(null);
    setVillesDepart([]);
    setNouvelleVille("");
    setNouvellesPlaces("");
    setNouvelleDate("");
    setError("");
  };

  const handleTarifChange = (index, field, value) => {
    const tarifs = [...form.tarifs];
    tarifs[index][field] = value;
    setForm({ ...form, tarifs });
  };
  const ajouterLigneTarif = () => {
    setForm({ ...form, tarifs: [...form.tarifs, { libelle: "", montant: "" }] });
  };
  const supprimerLigneTarif = (index) => {
    const tarifs = form.tarifs.filter((_, i) => i !== index);
    setForm({ ...form, tarifs: tarifs.length ? tarifs : [{ libelle: "", montant: "" }] });
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError("");
    try {
      if (editId) {
        await api.put("/oumrah/sessions/" + editId, {
          libelle: form.libelle,
          date_depart: form.date_depart || null,
          date_retour: form.date_retour || null,
          places_total: form.places_total ? parseInt(form.places_total) : 0,
          seuil_alerte: form.seuil_alerte ? parseInt(form.seuil_alerte) : 5,
          supplement_carte_sejour: form.supplement_carte_sejour ? parseFloat(form.supplement_carte_sejour) : 150,
          description: form.description,
        });
        // Enregistrer les quotas et dates des villes de départ affichés à l'écran
        for (const v of villesDepart.filter(villeModifiee)) {
          await modifierVille(v);
        }
        // Synchroniser les tarifs
        const tarifsValides = form.tarifs.filter((t) => t.libelle);
        const payload = (t) => ({
          libelle: t.libelle,
          montant: t.montant ? parseFloat(t.montant) : 0,
          type_chambre: t.type_chambre || null,
          ville_depart_id: t.ville_depart_id || null,
        });
        // Modifier existants + ajouter nouveaux
        for (const t of tarifsValides) {
          if (t.id) {
            await api.put("/oumrah/sessions/" + editId + "/tarifs/" + t.id, payload(t));
          } else {
            await api.post("/oumrah/sessions/" + editId + "/tarifs", payload(t));
          }
        }
        // Supprimer ceux retirés
        const idsRestants = tarifsValides.filter((t) => t.id).map((t) => t.id);
        for (const orig of tarifsOrigine) {
          if (!idsRestants.includes(orig.id)) {
            try { await api.delete("/oumrah/sessions/" + editId + "/tarifs/" + orig.id); } catch (e) { /* tarif utilisé */ }
          }
        }
      } else {
        const tarifsValides = form.tarifs.filter((t) => t.libelle);
        await api.post("/oumrah/sessions", {
          libelle: form.libelle,
          date_depart: form.date_depart || null,
          date_retour: form.date_retour || null,
          places_total: form.places_total ? parseInt(form.places_total) : 0,
          seuil_alerte: form.seuil_alerte ? parseInt(form.seuil_alerte) : 5,
          supplement_carte_sejour: form.supplement_carte_sejour ? parseFloat(form.supplement_carte_sejour) : 150,
          description: form.description,
          tarifs: tarifsValides.map((t) => ({
            libelle: t.libelle,
            montant: t.montant ? parseFloat(t.montant) : 0,
          })),
        });
      }
      fetchSessions();
      handleClose();
    } catch (err) {
      setError(err.response?.data?.message || "Erreur serveur");
    } finally {
      setLoading(false);
    }
  };

  const genererTexte = async (sessionId, type) => {
    setMessageLoading(true);
    setCopieOk("");
    try {
      const res = await api.get("/oumrah/sessions/" + sessionId + "/message/" + type);
      setMessageTexte(res.data.texte || "");
    } catch (err) {
      setMessageTexte("Erreur : impossible de générer le message.");
    } finally {
      setMessageLoading(false);
    }
  };

  const ouvrirMessage = async (session) => {
    setMessageSession(session);
    setMessageType("publicite");
    setMessageTexte("");
    await genererTexte(session.id, "publicite");
  };

  const changerType = async (type) => {
    setMessageType(type);
    if (messageSession) await genererTexte(messageSession.id, type);
  };

  // Convertit le HTML de l'éditeur en texte WhatsApp (gras *, italique _, barré ~)
  const versWhatsApp = (html) => {
    let t = html || "";
    t = t.replace(/<strong>(.*?)<\/strong>/gi, "*$1*").replace(/<b>(.*?)<\/b>/gi, "*$1*");
    t = t.replace(/<em>(.*?)<\/em>/gi, "_$1_").replace(/<i>(.*?)<\/i>/gi, "_$1_");
    t = t.replace(/<s>(.*?)<\/s>/gi, "~$1~").replace(/<del>(.*?)<\/del>/gi, "~$1~");
    t = t.replace(/<li>(.*?)<\/li>/gi, "- $1\n");
    t = t.replace(/<\/p>/gi, "\n").replace(/<br\s*\/?>/gi, "\n");
    t = t.replace(/<[^>]+>/g, "");
    t = t.replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&nbsp;/g, " ");
    return t.replace(/\n{3,}/g, "\n\n").trim();
  };

  const copier = async (format) => {
    const texte = format === "whatsapp" ? versWhatsApp(messageTexte) : messageTexte.replace(/<[^>]+>/g, "");
    try {
      await navigator.clipboard.writeText(texte);
      setCopieOk(format === "whatsapp" ? "Copié pour WhatsApp !" : "Copié !");
      setTimeout(() => setCopieOk(""), 2500);
    } catch (err) {
      setCopieOk("Erreur de copie");
    }
  };

  const telechargerExport = async () => {
    if (!exportSession) return;
    setExportLoading(true);
    try {
      const url = "/oumrah/sessions/" + exportSession.id + "/export/" + exportFormat +
        (exportStatut ? "?statut=" + exportStatut : "");
      const res = await api.get(url, { responseType: "blob" });
      const ext = exportFormat === "excel" ? "xlsx" : "pdf";
      const nomFichier = "oumrah_" + (exportSession.libelle || "session").replace(/[^a-zA-Z0-9]/g, "_") + "." + ext;
      const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = nomFichier;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
      setExportSession(null);
    } catch (err) {
      alert("Erreur lors de l'export");
    } finally {
      setExportLoading(false);
    }
  };

  const handleArchiver = async (id) => {
    if (window.confirm("Archiver cette session ?")) {
      try {
        await api.put("/oumrah/sessions/" + id + "/archiver");
        fetchSessions();
      } catch (err) {
        alert(err.response?.data?.message || "Erreur serveur");
      }
    }
  };

  const handleDesarchiver = async (id) => {
    try {
      await api.put("/oumrah/sessions/" + id + "/desarchiver");
      fetchSessions();
    } catch (err) {
      alert(err.response?.data?.message || "Erreur serveur");
    }
  };

  const handleSupprimerTarif = async (sessionId, tarifId) => {
    if (window.confirm("Supprimer ce tarif ?")) {
      try {
        await api.delete("/oumrah/sessions/" + sessionId + "/tarifs/" + tarifId);
        fetchSessions();
      } catch (err) {
        alert(err.response?.data?.message || "Erreur serveur");
      }
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2} mt={-3} py={3} px={2}
                variant="gradient" bgColor="info"
                borderRadius="lg" coloredShadow="info"
                display="flex" justifyContent="space-between" alignItems="center"
              >
                <MDTypography variant="h6" color="white">Sessions Oumrah</MDTypography>
                <MDBox display="flex" alignItems="center" gap={2}>
                  <FormControlLabel
                    control={<Switch checked={montrerArchivees} onChange={(e) => setMontrerArchivees(e.target.checked)} />}
                    label={<span style={{ color: "white", fontSize: "0.8rem" }}>Voir les archivées</span>}
                  />
                  <MDButton variant="outlined" color="white" size="small" onClick={() => handleOpen()}>
                    <Icon>add</Icon>&nbsp;Nouvelle session
                  </MDButton>
                </MDBox>
              </MDBox>
              <MDBox pt={3} px={3} pb={3}>
                <Grid container spacing={3}>
                  {sessions.map((s) => (
                    <Grid item xs={12} md={6} key={s.id}>
                      <Card sx={{ p: 2, border: "1px solid #eee" }}>
                        <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                          <MDTypography variant="h6">{s.libelle}</MDTypography>
                          <Chip
                            label={s.statut}
                            size="small"
                            sx={{ bgcolor: statutColor[s.statut] || "#9e9e9e", color: "white" }}
                          />
                        </MDBox>
                        <MDTypography variant="caption" color="text" display="block">
                          Départ : {s.date_depart ? new Date(s.date_depart).toLocaleDateString("fr-FR") : "—"}
                          {"  •  "}Retour : {s.date_retour ? new Date(s.date_retour).toLocaleDateString("fr-FR") : "—"}
                        </MDTypography>
                        {(s.villes_depart && s.villes_depart.length > 0) && (
                          <MDBox display="flex" flexWrap="wrap" gap={0.75} mt={1.5} mb={1}>
                            {s.villes_depart.map((v) => {
                              const restant = v.places_restantes;
                              const complet = restant <= 0;
                              const alerte = !complet && restant <= 5;
                              const couleur = complet
                                ? { bg: "#FCEBEB", fg: "#A32D2D" }
                                : alerte
                                ? { bg: "#FAEEDA", fg: "#854F0B" }
                                : { bg: "#EAF3DE", fg: "#3B6D11" };
                              return (
                                <MDBox key={v.id} sx={{ bgcolor: couleur.bg, borderRadius: 2, px: 1.25, py: 0.4 }}>
                                  <MDTypography variant="caption" sx={{ color: couleur.fg, fontWeight: "bold" }}>
                                    {libelleVilleDate(v.ville, v.date_depart)} : {Math.max(0, restant)} place{Math.max(0, restant) > 1 ? "s" : ""} restante{Math.max(0, restant) > 1 ? "s" : ""} / {v.places_total}
                                  </MDTypography>
                                </MDBox>
                              );
                            })}
                          </MDBox>
                        )}
                        {s.statut !== "archivee" && s.places_restantes <= 0 && (
                          <MDBox sx={{ bgcolor: "#fdecea", border: "1px solid #f5c6cb", borderRadius: "8px", px: 1.5, py: 0.75, mb: 1, display: "flex", alignItems: "center", gap: 1 }}>
                            <Icon sx={{ color: "#c0392b", fontSize: "18px !important" }}>block</Icon>
                            <MDTypography variant="caption" sx={{ color: "#c0392b", fontWeight: "bold" }}>
                              Session complète
                            </MDTypography>
                          </MDBox>
                        )}
                        {s.statut !== "archivee" && s.places_restantes > 0 && s.places_restantes <= (s.seuil_alerte ?? 5) && (
                          <MDBox sx={{ bgcolor: "#fff4e5", border: "1px solid #ffd9a0", borderRadius: "8px", px: 1.5, py: 0.75, mb: 1, display: "flex", alignItems: "center", gap: 1 }}>
                            <Icon sx={{ color: "#e67e22", fontSize: "18px !important" }}>warning</Icon>
                            <MDTypography variant="caption" sx={{ color: "#b9651a", fontWeight: "bold" }}>
                              Plus que {s.places_restantes} place(s) !
                            </MDTypography>
                          </MDBox>
                        )}

                        <MDBox mt={1} mb={1}>
                          <MDTypography variant="caption" fontWeight="bold" color="text">Tarifs :</MDTypography>
                          {s.tarifs?.length ? (
                            s.tarifs.map((t) => (
                              <MDBox key={t.id} display="flex" justifyContent="space-between" alignItems="center">
                                <MDTypography variant="caption" color="text">
                                  {t.libelle} — {fmt(t.montant)} €
                                </MDTypography>
                                <IconButton size="small" onClick={() => handleSupprimerTarif(s.id, t.id)}>
                                  <Icon fontSize="small">remove_circle</Icon>
                                </IconButton>
                              </MDBox>
                            ))
                          ) : (
                            <MDTypography variant="caption" color="text" display="block">Aucun tarif</MDTypography>
                          )}
                        </MDBox>

                        <MDBox display="flex" gap={1} flexWrap="wrap" mt={1}>
                          <MDButton size="small" color="info" variant="outlined" onClick={() => handleOpen(s)}>
                            <Icon>edit</Icon>&nbsp;Modifier
                          </MDButton>
                          <MDButton size="small" color="dark" variant="outlined" onClick={() => { setExportSession(s); setExportFormat("excel"); setExportStatut(""); }}>
                            <Icon>download</Icon>&nbsp;Exporter
                          </MDButton>
                          <MDButton size="small" color="success" variant="outlined" onClick={() => ouvrirMessage(s)}>
                            <Icon>campaign</Icon>&nbsp;Message
                          </MDButton>
                          {s.statut === "archivee" ? (
                            <MDButton size="small" color="success" variant="outlined" onClick={() => handleDesarchiver(s.id)}>
                              <Icon>unarchive</Icon>&nbsp;Désarchiver
                            </MDButton>
                          ) : (
                            <MDButton size="small" color="error" variant="outlined" onClick={() => handleArchiver(s.id)}>
                              <Icon>archive</Icon>&nbsp;Archiver
                            </MDButton>
                          )}
                        </MDBox>
                      </Card>
                    </Grid>
                  ))}
                  {sessions.length === 0 && (
                    <Grid item xs={12}>
                      <MDTypography variant="body2" color="text" textAlign="center">
                        Aucune session.
                      </MDTypography>
                    </Grid>
                  )}
                </Grid>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>{editId ? "Modifier la session" : "Nouvelle session Oumrah"}</DialogTitle>
        <DialogContent>
          <MDBox display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField label="Libellé (ex: Oumrah Ramadan 2026)" fullWidth
              value={form.libelle} onChange={(e) => setForm({ ...form, libelle: e.target.value })} />
            <TextField label="Date de départ" type="date" fullWidth InputLabelProps={{ shrink: true }}
              value={form.date_depart} onChange={(e) => setForm({ ...form, date_depart: e.target.value })} />
            <TextField label="Date de retour" type="date" fullWidth InputLabelProps={{ shrink: true }}
              value={form.date_retour} onChange={(e) => setForm({ ...form, date_retour: e.target.value })} />
            <TextField label="Nombre de places" type="number" fullWidth
              value={form.places_total} onChange={(e) => setForm({ ...form, places_total: e.target.value })} />
            <TextField label="Seuil d'alerte (places restantes)" type="number" fullWidth
              helperText="Une alerte s'affiche quand il reste ce nombre de places ou moins"
              value={form.seuil_alerte} onChange={(e) => setForm({ ...form, seuil_alerte: e.target.value })} />
            <TextField label="Supplément carte de séjour (€)" type="number" fullWidth
              helperText="Montant ajouté automatiquement pour les participants ayant besoin d'une carte de séjour"
              value={form.supplement_carte_sejour} onChange={(e) => setForm({ ...form, supplement_carte_sejour: e.target.value })} />
            <TextField label="Description" fullWidth multiline rows={2}
              value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />

            {editId && (
              <MDBox mt={2}>
                <MDTypography variant="caption" fontWeight="bold" color="text">
                  Villes de départ
                </MDTypography>
                <MDBox display="flex" flexWrap="wrap" gap={1} mt={1} mb={1}>
                  {villesDepart.map((v) => (
                    <MDBox key={v.id} display="flex" alignItems="center" gap={0.75}
                      sx={{ bgcolor: "#e3f2fd", borderRadius: 2, px: 1.5, py: 0.5 }}>
                      <MDTypography variant="caption" fontWeight="bold">{v.ville}</MDTypography>
                      <TextField
                        type="number"
                        size="small"
                        variant="standard"
                        value={v.places_saisie}
                        onChange={(e) => changerSaisieVille(v.id, "places_saisie", e.target.value)}
                        onBlur={() => enregistrerSaisieVille(v)}
                        InputProps={{ disableUnderline: false }}
                        inputProps={{ min: 0, style: { width: 46, textAlign: "center", fontSize: 12 } }}
                        sx={{ mx: 0.25 }}
                      />
                      <MDTypography variant="caption" color="text">pl.</MDTypography>
                      <TextField
                        type="date"
                        size="small"
                        variant="standard"
                        value={v.date_saisie || ""}
                        onChange={(e) => changerSaisieVille(v.id, "date_saisie", e.target.value)}
                        onBlur={() => enregistrerSaisieVille(v)}
                        title="Date de départ"
                        error={!v.date_saisie}
                        inputProps={{ style: { width: 118, fontSize: 12 } }}
                        sx={{ mx: 0.25 }}
                      />
                      <IconButton size="small" onClick={() => supprimerVilleDepart(v.id)} sx={{ p: 0.2 }}>
                        <Icon fontSize="small">close</Icon>
                      </IconButton>
                    </MDBox>
                  ))}
                  {villesDepart.length === 0 && (
                    <MDTypography variant="caption" color="text">Aucune ville de départ déclarée</MDTypography>
                  )}
                </MDBox>
                <MDBox display="flex" gap={1} alignItems="center">
                  <TextField label="Nouvelle ville" size="small" value={nouvelleVille}
                    onChange={(e) => setNouvelleVille(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && ajouterVilleDepart()} />
                  <TextField label="Places" type="number" size="small" value={nouvellesPlaces}
                    onChange={(e) => setNouvellesPlaces(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && ajouterVilleDepart()}
                    inputProps={{ min: 0 }} sx={{ width: 90 }} />
                  <TextField label="Date de départ" type="date" size="small" value={nouvelleDate}
                    onChange={(e) => setNouvelleDate(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && ajouterVilleDepart()}
                    InputLabelProps={{ shrink: true }} sx={{ width: 160 }} />
                  <MDButton size="small" color="info" variant="outlined" onClick={ajouterVilleDepart}>
                    <Icon>add</Icon>&nbsp;Ajouter
                  </MDButton>
                </MDBox>
              </MDBox>
            )}
            {!editId && (
              <MDBox mt={2}>
                <MDTypography variant="caption" color="text">
                  Les villes de départ pourront être ajoutées après création de la session.
                </MDTypography>
              </MDBox>
            )}
            {true && (
              <MDBox mt={2}>
                <MDTypography variant="caption" fontWeight="bold" color="text">
                  Tarifs (adulte, enfant, type de chambre...)
                </MDTypography>
                {form.tarifs.map((t, i) => (
                  <MDBox key={i} display="flex" gap={1} alignItems="center" mt={1}>
                    <TextField label="Libellé tarif" size="small" value={t.libelle}
                      onChange={(e) => handleTarifChange(i, "libelle", e.target.value)} />
                    <TextField label="Montant €" type="number" size="small" value={t.montant}
                      onChange={(e) => handleTarifChange(i, "montant", e.target.value)} />
                    <FormControl size="small" sx={{ minWidth: 130 }}>
                      <InputLabel id={"chambre-" + i}>Chambre</InputLabel>
                      <Select labelId={"chambre-" + i} label="Chambre" sx={{ height: 40 }}
                        value={t.type_chambre || ""}
                        onChange={(e) => handleTarifChange(i, "type_chambre", e.target.value)}>
                        <MenuItem value="">—</MenuItem>
                        <MenuItem value="double">Double (2)</MenuItem>
                        <MenuItem value="triple">Triple (3)</MenuItem>
                        <MenuItem value="quadruple">Quadruple (4)</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 170 }}>
                      <InputLabel id={"ville-" + i}>Ville départ</InputLabel>
                      <Select labelId={"ville-" + i} label="Ville départ" sx={{ height: 40 }}
                        value={t.ville_depart_id || ""}
                        onChange={(e) => handleTarifChange(i, "ville_depart_id", e.target.value)}>
                        <MenuItem value="">—</MenuItem>
                        {villesDepart.map((v) => (
                          <MenuItem key={v.id} value={v.id}>{libelleVilleDate(v.ville, v.date_depart)}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <IconButton size="small" onClick={() => supprimerLigneTarif(i)}>
                      <Icon>remove_circle</Icon>
                    </IconButton>
                  </MDBox>
                ))}
                <MDButton size="small" color="info" variant="text" onClick={ajouterLigneTarif} sx={{ mt: 1 }}>
                  <Icon>add</Icon>&nbsp;Ajouter un tarif
                </MDButton>
              </MDBox>
            )}



            {error && <MDTypography variant="caption" color="error">{error}</MDTypography>}
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleClose} color="secondary">Annuler</MDButton>
          <MDButton onClick={handleSubmit} color="info" disabled={loading || !form.libelle}>
            {loading ? "Enregistrement..." : "Enregistrer"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* DIALOG EXPORT */}
      <Dialog open={!!exportSession} onClose={() => setExportSession(null)} fullWidth maxWidth="xs">
        <DialogTitle>Exporter les participants</DialogTitle>
        <DialogContent>
          <MDTypography variant="caption" color="text" display="block" mb={2}>
            {exportSession?.libelle}
          </MDTypography>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="export-format-label">Format</InputLabel>
            <Select labelId="export-format-label" label="Format" sx={{ height: 44 }}
              value={exportFormat} onChange={(e) => setExportFormat(e.target.value)}>
              <MenuItem value="excel">Excel (.xlsx)</MenuItem>
              <MenuItem value="pdf">PDF</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel id="export-statut-label">Inscriptions à inclure</InputLabel>
            <Select labelId="export-statut-label" label="Inscriptions à inclure" sx={{ height: 44 }}
              value={exportStatut} onChange={(e) => setExportStatut(e.target.value)}>
              <MenuItem value="">Toutes</MenuItem>
              <MenuItem value="confirmee">Confirmées</MenuItem>
              <MenuItem value="devis_accepte">Devis accepté</MenuItem>
              <MenuItem value="devis_envoye">Devis envoyé</MenuItem>
              <MenuItem value="validee">Validées</MenuItem>
              <MenuItem value="en_attente">En attente</MenuItem>
              <MenuItem value="annulee">Annulées</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setExportSession(null)} color="secondary">Annuler</MDButton>
          <MDButton onClick={telechargerExport} color="info" disabled={exportLoading}>
            <Icon>download</Icon>&nbsp;{exportLoading ? "Génération..." : "Télécharger"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* DIALOG APERCU MESSAGE */}
      <Dialog open={!!messageSession} onClose={() => setMessageSession(null)} fullWidth maxWidth="sm">
        <DialogTitle>Message — {messageSession?.libelle}</DialogTitle>
        <DialogContent dividers>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="msg-type-label">Type de message</InputLabel>
            <Select labelId="msg-type-label" label="Type de message" sx={{ height: 44 }}
              value={messageType} onChange={(e) => changerType(e.target.value)}>
              <MenuItem value="publicite">Publicité de session</MenuItem>
              <MenuItem value="relance_places">Relance — peu de places</MenuItem>
              <MenuItem value="relance_complete">Annonce — session complète</MenuItem>
            </Select>
          </FormControl>
          {messageLoading ? (
            <MDTypography variant="body2" color="text">Génération...</MDTypography>
          ) : (
            <>
              <MDTypography variant="caption" color="text" display="block" mb={1}>
                Vous pouvez ajuster le message avant de le copier :
              </MDTypography>
              <TextField
                multiline
                minRows={6}
                fullWidth
                value={messageTexte.replace(/<[^>]+>/g, "")}
                onChange={(e) => setMessageTexte(e.target.value)}
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ flexWrap: "wrap", gap: 1 }}>
          {copieOk && (
            <MDTypography variant="caption" color="success" sx={{ mr: "auto", ml: 1 }}>{copieOk}</MDTypography>
          )}
          <MDButton color="success" variant="outlined" onClick={() => copier("whatsapp")} disabled={messageLoading}>
            <Icon>chat</Icon>&nbsp;Copier pour WhatsApp
          </MDButton>
          <MDButton color="info" variant="outlined" onClick={() => copier("email")} disabled={messageLoading}>
            <Icon>mail</Icon>&nbsp;Copier pour email
          </MDButton>
          <MDButton color="secondary" onClick={() => setMessageSession(null)}>Fermer</MDButton>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default OumrahSessions;
